// GA4 conversion tracking + UTM preservation + page-level attribution.
//
// • `categorizePath(path)` — maps a URL path to a high-level page_category and
//   (where applicable) a city / state, so every conversion event carries
//   landing-page provenance.
// • `captureAttribution(location)` — runs on first mount, stores first-touch
//   UTM + landing path in sessionStorage. Cheap and idempotent.
// • `getAttribution()` — returns the persisted snapshot for use in events and
//   form submissions.
// • `trackEvent(name, params)` — thin wrapper around window.gtag('event', …).
//   Silently no-ops when GA4 isn't loaded (admin-controlled via settings).
// • `trackCTA(name, ctxOverride)` — convenience for click handlers; auto-attaches
//   the current page's attribution.

const SS_KEY = "at_attribution_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];

// Metros that have their own /online-tuition-<metro> route (handled by OnlineCity, not the V7 Tier-2 batch).
const METROS = new Set([
    "mumbai", "delhi", "bangalore", "pune", "hyderabad", "chennai",
    "kolkata", "ahmedabad", "indore", "delhi-ncr",
]);

const TIER2_SLUGS = new Set([
    "lucknow", "jaipur", "patna", "kanpur", "surat", "vadodara", "coimbatore",
    "kochi", "thiruvananthapuram", "raipur", "dehradun", "ranchi", "visakhapatnam",
    "mysore", "agra", "varanasi", "aurangabad", "madurai", "nashik", "amritsar",
    "jamshedpur", "gwalior", "allahabad", "jodhpur", "srinagar", "faridabad",
    "ludhiana", "meerut", "rajkot", "guwahati",
]);

const STATE_SLUGS = new Set([
    "andhra-pradesh", "arunachal-pradesh", "assam", "bihar", "chhattisgarh", "goa",
    "gujarat", "haryana", "himachal-pradesh", "jharkhand", "karnataka", "kerala",
    "madhya-pradesh", "maharashtra", "manipur", "meghalaya", "mizoram", "nagaland",
    "odisha", "punjab", "rajasthan", "sikkim", "tamil-nadu", "telangana", "tripura",
    "uttar-pradesh", "uttarakhand", "west-bengal", "delhi", "chandigarh", "jammu-kashmir",
]);

const NEAR_ME_SLUGS = new Set([
    "online-tuition-near-me", "online-classes-near-me", "online-tutor-near-me",
    "1-to-1-online-classes-india", "personal-online-tutor-india", "live-online-tuition-india",
    // V8 offline Near-Me pillars
    "home-tutor-near-me", "home-tuition-near-me",
]);

/**
 * Map a URL path to { page_category, city, state, focus_kw }.
 * page_category is one of:
 *   near-me | state | tier2-city | metro-city | jabalpur | bhopal | nagpur
 *   | 1-to-1-home-tuition | 1-to-1-online-classes | hub | other
 */
export function categorizePath(rawPath) {
    const path = (rawPath || "/").split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
    const slug = path.replace(/^\/+/, "");

    // Hub pages
    if (slug === "online-tuition-india-states" || slug === "online-tuition-india-cities") {
        return { page_category: "hub", city: null, state: null };
    }

    // Core 3 cities (highest business priority)
    if (slug === "jabalpur" || slug.endsWith("-jabalpur")) {
        return { page_category: "jabalpur", city: "Jabalpur", state: "Madhya Pradesh" };
    }
    if (slug === "bhopal" || slug.endsWith("-bhopal")) {
        return { page_category: "bhopal", city: "Bhopal", state: "Madhya Pradesh" };
    }
    if (slug === "nagpur" || slug.endsWith("-nagpur")) {
        return { page_category: "nagpur", city: "Nagpur", state: "Maharashtra" };
    }

    // Near-Me pillar pages (V7)
    if (NEAR_ME_SLUGS.has(slug)) {
        return { page_category: "near-me", city: null, state: null };
    }

    // State pages: /online-tuition-in-<state>
    const stateMatch = slug.match(/^online-tuition-in-([a-z-]+)$/);
    if (stateMatch && STATE_SLUGS.has(stateMatch[1])) {
        return { page_category: "state", city: null, state: stateMatch[1] };
    }

    // /online-tuition-<city>  (Tier-2 V7 batch OR metro OnlineCity routes)
    const cityMatch = slug.match(/^online-tuition-([a-z-]+)$/);
    if (cityMatch) {
        const c = cityMatch[1];
        if (TIER2_SLUGS.has(c)) return { page_category: "tier2-city", city: c, state: null };
        if (METROS.has(c)) return { page_category: "metro-city", city: c, state: null };
    }
    // /home-tuition-<metro>
    const homeMetroMatch = slug.match(/^home-tuition-([a-z-]+)$/);
    if (homeMetroMatch && METROS.has(homeMetroMatch[1])) {
        return { page_category: "metro-city", city: homeMetroMatch[1], state: null };
    }

    // Home-tuition family
    if (slug.includes("home-tuition") || slug.includes("home-tutor") || slug === "online-tuition-vs-coaching-classes-india") {
        return { page_category: "1-to-1-home-tuition", city: null, state: null };
    }

    // Online-classes family
    if (slug === "online-classes" || slug.startsWith("online-") || slug.startsWith("1-to-1-online")) {
        return { page_category: "1-to-1-online-classes", city: null, state: null };
    }

    return { page_category: "other", city: null, state: null };
}

/**
 * Capture first-touch UTM + landing page on first session pageview.
 * Idempotent — once captured, the original landing snapshot stays for the session.
 */
export function captureAttribution(location) {
    if (typeof window === "undefined") return null;
    try {
        const existing = sessionStorage.getItem(SS_KEY);
        if (existing) return JSON.parse(existing);

        const url = new URL(window.location.href);
        const utm = {};
        for (const k of UTM_KEYS) {
            const v = url.searchParams.get(k);
            if (v) utm[k] = v;
        }
        const referrer = document.referrer || "";
        const landingPath = (location && location.pathname) || url.pathname;
        const cat = categorizePath(landingPath);

        const snapshot = {
            landing_path: landingPath,
            landing_page_category: cat.page_category,
            landing_city: cat.city,
            landing_state: cat.state,
            referrer,
            session_started_at: new Date().toISOString(),
            ...utm,
        };
        sessionStorage.setItem(SS_KEY, JSON.stringify(snapshot));
        return snapshot;
    } catch (_e) {
        return null;
    }
}

export function getAttribution() {
    if (typeof window === "undefined") return {};
    try {
        const raw = sessionStorage.getItem(SS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (_e) {
        return {};
    }
}

function _currentContext() {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const cat = categorizePath(path);
    return {
        page_path: path,
        page_category: cat.page_category,
        page_city: cat.city,
        page_state: cat.state,
    };
}

/**
 * Fire a GA4 event with merged attribution. Safe to call before gtag loads —
 * the dataLayer will buffer until the snippet initialises. Bootstraps
 * window.dataLayer if absent so events are always inspectable for QA.
 */
export function trackEvent(name, params = {}) {
    if (typeof window === "undefined") return;
    const attribution = getAttribution();
    const ctx = _currentContext();
    const payload = {
        ...attribution,
        ...ctx,
        ...params,
        event_ts: new Date().toISOString(),
    };
    try {
        if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
        if (typeof window.gtag === "function") {
            window.gtag("event", name, payload);
        }
        // Always push to dataLayer so events are inspectable even before GA4 loads.
        window.dataLayer.push({ event: name, ...payload });
    } catch (_e) { /* never throw from analytics */ }
}

/**
 * Convenience for CTA click handlers — fires the event with the current
 * page attribution. ctxOverride lets a CTA tag itself (e.g., cta_location).
 */
export function trackCTA(eventName, ctxOverride = {}) {
    trackEvent(eventName, ctxOverride);
}

/**
 * Build the attribution payload to attach to backend form submissions so we
 * can build organic-conversion reports inside the admin dashboard (independent
 * of GA4 sampling / consent loss).
 */
export function attributionForSubmission() {
    const att = getAttribution();
    const ctx = _currentContext();
    return {
        landing_path: att.landing_path || ctx.page_path,
        landing_page_category: att.landing_page_category || ctx.page_category,
        landing_city: att.landing_city || ctx.page_city,
        landing_state: att.landing_state || ctx.page_state,
        submit_path: ctx.page_path,
        submit_page_category: ctx.page_category,
        submit_city: ctx.page_city,
        submit_state: ctx.page_state,
        referrer: att.referrer || (typeof document !== "undefined" ? document.referrer : "") || "",
        utm_source: att.utm_source || null,
        utm_medium: att.utm_medium || null,
        utm_campaign: att.utm_campaign || null,
        utm_term: att.utm_term || null,
        utm_content: att.utm_content || null,
        gclid: att.gclid || null,
        fbclid: att.fbclid || null,
        session_started_at: att.session_started_at || null,
    };
}
